import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppEquipmentTypeSelectField, AppInputField, AppPromptsField } from '@/form/FormUI';
import type { ImageNodeType, NodeFormProps } from '@/nodes';
import { equipmentTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { ImageDataSchema, type ImageDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof ImageDataSchema>;

export const ImageForm: FC<NodeFormProps<ImageNodeType>> = ({ spaceId, flowId, node, updateNode, cancelUpdate }) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, ImageDataType>({
        resolver: zodResolver(ImageDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: ImageDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What message are we showing the user?" />

                    <AppEquipmentTypeSelectField
                        control={form.control}
                        name="equipmentTypeId"
                        label="Equipment Image Default"
                        placeholder="Select Equipment Type"
                        description="Select the Equipment Type to show images for"
                        equipmentTypes={equipmentTypes}
                        allowContextSelection
                    />

                    {/*<AppInputField*/}
                    {/*    label="Button Label"*/}
                    {/*    placeholder="Continue"*/}
                    {/*    control={form.control}*/}
                    {/*    name="buttonLabel"*/}
                    {/*    description="The label on the button to continue."*/}
                    {/*/>*/}
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};
